import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

import { nanoid } from 'nanoid';
import { getTagData } from 'api/tags';
import { getExpressionData } from 'api/expression';
import useApp from 'hooks/useApp';

// -------------------------
// Types
// -------------------------
export type WidgetType =
  | 'large'
  | 'gaugeBar'
  | 'gauge'
  | 'area'
  | 'gaugePage'
  | 'column'
  | 'kpi';

export interface WidgetConfig {
  /** Friendly name shown in the header */
  name: string;
  /** Ordered tag ids the user mapped while creating the widget */
  tagIds: number[];
  /** Custom names for each tag (optional, falls back to original tag names) */
  tagNames?: string[];
  /** Optional UOM required by Column widget */
  uom?: string;
}

export interface DxfWidget {
  id: string;
  type: WidgetType;
  config: WidgetConfig;
  /** Absolute world-space position */
  x: number;
  y: number;
  /** Render scale (not pixel size). 1 === 100 % */
  scale: number;
  /** Whether widget has fixed size (doesn't scale with zoom) */
  fixedSize?: boolean;
}

interface WidgetDataMap {
  /** widgetId → { tagId → raw API response } */
  [widgetId: string]: Record<string, any>;
}

interface DxfWidgetsContextShape {
  widgets: DxfWidget[];
  widgetData: WidgetDataMap;
  addWidget: (partial: Omit<Partial<DxfWidget>, 'id' | 'scale'> & { type: WidgetType; config: WidgetConfig }) => void;
  updateWidget: (widgetId: string, type: WidgetType, config: WidgetConfig) => void;
  updateWidgetPosition: (widgetId: string, x: number, y: number) => void;
  updateWidgetScale: (widgetId: string, scale: number) => void;
  updateWidgetFixedSize: (widgetId: string, fixedSize: boolean) => void;
  removeWidget: (widgetId: string) => void;
  clearAllWidgets: () => void;
  getWorldToCanvas: () => ((worldX: number, worldY: number) => { x: number; y: number }) | null;
  setWorldToCanvas: (fn: (worldX: number, worldY: number) => { x: number; y: number }) => void;
  setWidgetContextMenu: (widgetId: string, screenX: number, screenY: number) => void;
  currentZoom: number;
  setCurrentZoom: (zoom: number) => void;
  getWidgetDataForWidget: (widgetId: string, widgetType: string) => Record<string, any>;
}

const DxfWidgetsContext = createContext<DxfWidgetsContextShape | null>(null);

export const useDxfWidgets = () => {
  const ctx = useContext(DxfWidgetsContext);
  if (!ctx) throw new Error('useDxfWidgets must be used inside <DxfWidgetsProvider>');
  return ctx;
};

/**
 * Centralised provider handling widget list + data fetching
 */
export const DxfWidgetsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [widgets, setWidgets] = useState<DxfWidget[]>([]);
  const [widgetData, setWidgetData] = useState<WidgetDataMap>({});
  const [currentZoom, setCurrentZoom] = useState(1);
  // Import useApp hook to access tab data and update functions
  const { activeTabId, tabsData, updateTabData } = useApp();
  // Ref to track if we're currently moving or resizing a widget
  const isModifyingWidgetRef = useRef(false);
  // Ref to store the worldToCanvas function
  const worldToCanvasRef = useRef<((worldX: number, worldY: number) => { x: number; y: number }) | null>(null);
  // State to track widget resizing
  const [isWidgetResizing, setIsWidgetResizing] = useState(false);
  const [widgetResizingId, setWidgetResizingId] = useState<string | null>(null);

  // Function to get the worldToCanvas function
  const getWorldToCanvas = useCallback(() => {
    return worldToCanvasRef.current;
  }, []);

  // Function to set the worldToCanvas function
  const setWorldToCanvas = useCallback((fn: (worldX: number, worldY: number) => { x: number; y: number }) => {
    worldToCanvasRef.current = fn;
  }, []);


  /** Add new widget – caller provides type, config & target position */
  const addWidget: DxfWidgetsContextShape['addWidget'] = useCallback(
    ({ type, config, x = 0, y = 0 }) => {
      setWidgets((prev) => {
        const newWidget = {
          id: nanoid(),
          type,
          config,
          x,
          y,
          scale: 0.5,
        };
        return [...prev, newWidget];
      });
    },
    [],
  );

  /** Update existing widget – caller provides widget ID, type, and config */
  const updateWidget: DxfWidgetsContextShape['updateWidget'] = useCallback(
    (widgetId: string, type: WidgetType, config: WidgetConfig) => {
      setWidgets((prev) => {
        const updated = prev.map(w =>
          w && w.id === widgetId
            ? { ...w, type, config }
            : w
        );
        return updated;
      });

      // Clear the fetched flag for this widget to trigger data refresh
      if (fetchedWidgetsRef.current[widgetId]) {
        delete fetchedWidgetsRef.current[widgetId];
      }

      // Clear cached data for this widget to force fresh data fetch
      if (widgetData[widgetId]) {
        setWidgetData(prev => {
          const newData = { ...prev };
          delete newData[widgetId];
          return newData;
        });
      }

      // Clear the fetched flag for this widget to trigger data refresh
      if (fetchedWidgetsRef.current[widgetId]) {
        delete fetchedWidgetsRef.current[widgetId];
      }

      // Clear cached data for this widget to force fresh data fetch
      if (widgetData[widgetId]) {
        setWidgetData(prev => {
          const newData = { ...prev };
          delete newData[widgetId];
          return newData;
        });
      }
    },
    [widgetData],
  );

  /** Update widget position */
  const updateWidgetPosition = useCallback((widgetId: string, x: number, y: number) => {
    setWidgets(prev => prev.map(w => w && w.id === widgetId ? { ...w, x, y } : w));
  }, []);

  /** Update widget scale */
  const updateWidgetScale = useCallback((widgetId: string, scale: number) => {
    // Set the modifying flag to prevent saving during scale operations
    isModifyingWidgetRef.current = true;

    setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, scale } : w));

    // Use setTimeout to clear the flag after the current event loop
    setTimeout(() => {
      isModifyingWidgetRef.current = false;
    }, 100);
  }, []);


  /** Update widget fixed size */
  const updateWidgetFixedSize = useCallback((widgetId: string, fixedSize: boolean) => {
    // This is typically a one-time operation, so no need for the modifying flag
    setWidgets(prev => prev.map(w => w && w.id === widgetId ? { ...w, fixedSize } : w));
  }, []);

  /** Remove widget */
  const removeWidget = useCallback((widgetId: string) => {
    setWidgets(prev => prev.filter(w => w && w.id !== widgetId));
    setPersistentGaugeData(prev => {
      const newData = { ...prev };
      delete newData[widgetId];
      return newData;
    });
  }, []);

  /** Clear all widgets */
  const clearAllWidgets = useCallback(() => {
    setWidgets([]);
    setWidgetData({});
    setPersistentGaugeData({});
  }, []);

  /** Set widget context menu (placeholder for now) */
  const setWidgetContextMenu = useCallback((widgetId: string, screenX: number, screenY: number) => {
    // Call the handler if it exists
    if ((setWidgetContextMenu as any).__handler) {
      (setWidgetContextMenu as any).__handler(widgetId, screenX, screenY);
    }
  }, []);

  // Load widgets from current tab when the active tab changes
  useEffect(() => {
    if (!activeTabId) {
      return;
    }

    const currentTab = tabsData.find((tab: any) => tab && tab.id === activeTabId);
    if (!currentTab) {
      return;
    }

    if (currentTab.type !== 'dxf-viewer') {
      return;
    }

    // If the tab has widgets stored, load them
    if (currentTab.widgets && currentTab.widgets.length > 0) {
      // Clear widget data first to prevent stale data
      setWidgetData({});

      // Set the widgets
      setWidgets(currentTab.widgets);
    } else {
      setWidgets([]);
      setWidgetData({});
    }

    // Reset the modifying flag when changing tabs
    isModifyingWidgetRef.current = false;
  }, [activeTabId, tabsData]);

  // Track the last time we saved widgets to prevent excessive API calls
  const lastSaveTimeRef = useRef<number>(0);
  const pendingSaveRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedWidgetsRef = useRef<string>('');

  // Save widgets to backend with debouncing
  const saveWidgetsToBackend = useCallback(() => {
    if (!activeTabId) {
      return;
    }

    const currentTab = tabsData.find((tab: any) => tab && tab.id === activeTabId);
    if (!currentTab) {
      return;
    }

    if (currentTab.type !== 'dxf-viewer') {
      return;
    }

    // Compare current widgets with last saved widgets to avoid unnecessary updates
    const newJson = JSON.stringify(widgets);

    if (lastSavedWidgetsRef.current === newJson) {
      return;
    }

    // Use a try-catch to handle any errors during the save
    try {
      updateTabData(activeTabId, { widgets });
      // Update the last saved widgets reference
      lastSavedWidgetsRef.current = newJson;
    } catch (error) {
      // Handle save error silently
    }
  }, [widgets, activeTabId, tabsData, updateTabData]);

  // Save widgets to backend whenever they change, with debounce
  useEffect(() => {
    // Skip if we don't have widgets or are currently modifying
    if (widgets.length === 0 || !activeTabId) return;

    // Skip saving while moving or resizing to avoid excessive API calls
    if (isModifyingWidgetRef.current) {
      return;
    }

    // Clear any pending save
    if (pendingSaveRef.current) {
      clearTimeout(pendingSaveRef.current);
    }

    // Check if we've saved recently (within 2 seconds)
    const now = Date.now();
    const timeSinceLastSave = now - lastSaveTimeRef.current;

    if (timeSinceLastSave < 2000) {
      // If we've saved recently, schedule a save after a delay
      pendingSaveRef.current = setTimeout(() => {
        saveWidgetsToBackend();
        lastSaveTimeRef.current = Date.now();
        pendingSaveRef.current = null;
      }, 2000);
    } else {
      // If we haven't saved recently, save immediately
      saveWidgetsToBackend();
      lastSaveTimeRef.current = now;
    }

    // Clean up any pending save on unmount
    return () => {
      if (pendingSaveRef.current) {
        clearTimeout(pendingSaveRef.current);
      }
    };
  }, [widgets, activeTabId, saveWidgetsToBackend]);

  /**
   * Whenever widgets change (added / removed / tag-mapping changed) ->
   * build the combined ID lists and fetch once.
   */
  // Store a reference to the fetch retry count
  const fetchRetryCountRef = useRef<Record<string, number>>({});

  // Cache for tag data to reduce API calls
  const tagDataCacheRef = useRef<Record<number, any>>({});
  const lastFetchTimeRef = useRef<number>(0);

  // Persistent gauge data state - keeps gauge data even during refreshes
  const [persistentGaugeData, setPersistentGaugeData] = useState<Record<string, Record<string, any>>>({});

  // Function to fetch tag data for widgets
  const fetchTagData = useCallback(async (widgetsToFetch: DxfWidget[], forceRefresh: boolean = false, silent: boolean = false) => {
    if (!widgetsToFetch || widgetsToFetch.length === 0) return;

    // Always run silently - no loading states

    try {
      // Collect all tag IDs from the widgets
      const tagIds: number[] = [];
      const widgetTagMatrix: Record<string, number[]> = {};

      widgetsToFetch.forEach((w) => {
        if (w && w.id && w.config && w.config.tagIds) {
          widgetTagMatrix[w.id] = w.config.tagIds;
          tagIds.push(...w.config.tagIds);
        }
      });

      // Filter out IDs we already have in the cache (unless force refresh)
      const uniqueTagIds = Array.from(new Set(tagIds))
        .filter((id) => !Number.isNaN(id))
        .filter((id) => forceRefresh || !tagDataCacheRef.current[id]);


      const tagData: Record<number, any> = { ...tagDataCacheRef.current };

      // Only fetch if we have new IDs and haven't fetched recently (or force refresh)
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchTimeRef.current;
      const minInterval = forceRefresh ? 500 : 1000; // Shorter interval for force refresh

      if (uniqueTagIds.length > 0 && timeSinceLastFetch > minInterval) {
        // Fetch the tag data
        const tagRes = await getTagData(uniqueTagIds);
        lastFetchTimeRef.current = Date.now();

        if (!tagRes || !tagRes.data) {
          // Handle invalid response silently
        } else {
          // Add new data to cache
          tagRes.data.forEach((item: any) => {
            if (item && typeof item === 'object' && item.id !== undefined && item.id !== null) {
              tagDataCacheRef.current[item.id] = item;
              tagData[item.id] = item;
            }
          });
        }
      }

      // Process the tag data for each widget using cache
      const newWidgetData = { ...widgetData };
      const newPersistentGaugeData = { ...persistentGaugeData };

      widgetsToFetch.forEach((w) => {
        if (!w || !w.id || !w.config || !w.config.tagIds) {
          return;
        }

        const byTag: Record<string, any> = {};
        w.config.tagIds.forEach((id) => {
          if (tagData[id] && tagData[id].id) {
            byTag[id] = tagData[id];
          }
        });

        // Only update if we found data for at least one tag
        if (Object.keys(byTag).length > 0) {
          // Always update regular widget data for all widgets
          newWidgetData[w.id] = byTag;

          // For gauge and gaugeBar widgets, only update persistent data on initial load (not during force refresh)
          if ((w.type === 'gauge' || w.type === 'gaugeBar') && !forceRefresh) {
            newPersistentGaugeData[w.id] = byTag;
          }
        }
      });

      // Update persistent gauge data (only on initial load)
      if (!forceRefresh) {
        setPersistentGaugeData(newPersistentGaugeData);
      }

      // Always update main widget data to ensure other widgets get fresh data
      setWidgetData(newWidgetData);

      // Reset retry counts for successfully fetched widgets
      widgetsToFetch.forEach(w => {
        if (w && w.id) {
          fetchRetryCountRef.current[w.id] = 0;
        }
      });

      // No loading states to clear - running silently

    } catch (err) {
      // Increment retry count for each widget
      widgetsToFetch.forEach(w => {
        if (w && w.id) {
          fetchRetryCountRef.current[w.id] = (fetchRetryCountRef.current[w.id] || 0) + 1;
        }
      });

      // Schedule a retry for widgets that haven't exceeded max retries
      const widgetsToRetry = widgetsToFetch.filter(w =>
        w && w.id && (fetchRetryCountRef.current[w.id] || 0) < 3
      );

      if (widgetsToRetry.length > 0 && widgetsToRetry.length < widgetsToFetch.length) {
        setTimeout(() => {
          // Check if any widget is being resized before retrying
          if (!isWidgetResizing) {
            fetchTagData(widgetsToRetry);
          }
        }, 3000); // Retry after 3 seconds with exponential backoff
      }
    }
  }, [widgetData]);

  // Track if we've already fetched data for specific widgets
  const fetchedWidgetsRef = useRef<Record<string, boolean>>({});

  // Effect to fetch data when widgets change - but not when widgetData changes
  useEffect(() => {
    if (!widgets || widgets.length === 0) return;

    // Skip API calls if any widget is being resized
    if (isWidgetResizing) {
      return;
    }

    // Find widgets that need data fetching and haven't been fetched already
    const widgetsNeedingData = widgets.filter(w => {
      // Skip if widget is invalid
      if (!w || !w.id || !w.config || !w.config.tagIds) return false;

      // Skip if we've already tried to fetch this widget's data
      if (fetchedWidgetsRef.current[w.id]) return false;

      // Check if we already have data for this widget
      const hasData = w.config.tagIds.some(id => widgetData[w.id]?.[id]);
      return !hasData;
    });

    if (widgetsNeedingData.length > 0) {
      // Mark these widgets as fetched to prevent repeated attempts
      widgetsNeedingData.forEach(w => {
        if (w && w.id) {
          fetchedWidgetsRef.current[w.id] = true;
        }
      });

      fetchTagData(widgetsNeedingData);
    }

    // Reset the fetched widgets ref when widgets array changes length
    return () => {
      if (widgets && widgets.length !== Object.keys(fetchedWidgetsRef.current).length) {
        fetchedWidgetsRef.current = {};
      }
    };
  }, [widgets, fetchTagData, isWidgetResizing]);

  // Periodic refresh of widget data every 15 seconds (similar to labels polling)
  useEffect(() => {
    if (!activeTabId || !widgets || widgets.length === 0) return;

    const interval = setInterval(() => {
      // Skip API calls if any widget is being resized
      if (isWidgetResizing) {
        return;
      }

      // Force refresh to get fresh data while keeping existing data visible (silent mode)
      if (widgets && widgets.length > 0) {
        fetchTagData(widgets, true, true); // silent = true
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [activeTabId, widgets, fetchTagData, isWidgetResizing]);

  // Function to get widget data - uses persistent data for gauges, regular data for others
  const getWidgetDataForWidget = useCallback((widgetId: string, widgetType: string) => {
    try {
      if (widgetType === 'gauge' || widgetType === 'gaugeBar') {
        // For gauge widgets, use persistent data if available, otherwise fall back to regular data
        return persistentGaugeData[widgetId] || widgetData[widgetId] || {};
      }
      return widgetData[widgetId] || {};
    } catch (error) {
      return {};
    }
  }, [persistentGaugeData, widgetData]);

  return (
    <DxfWidgetsContext.Provider value={{
      widgets,
      widgetData,
      addWidget,
      updateWidget,
      updateWidgetPosition,
      updateWidgetScale,
      updateWidgetFixedSize,
      removeWidget,
      clearAllWidgets,
      getWorldToCanvas,
      setWorldToCanvas,
      setWidgetContextMenu,
      currentZoom,
      setCurrentZoom,
      getWidgetDataForWidget
    }}>
      {children}
    </DxfWidgetsContext.Provider>
  );
};
